import React, { useState, useEffect, useRef, useMemo } from "react";
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
import { useDashboard } from "./dashboardStore";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const ExpandedRow = ({ expandedUser }) => {

  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    }
  }, [expandedUser]);

 return (
 
    <tr>
      <td colSpan={7} className="p-2 bg-gray-50 border">
        <div
          ref={contentRef}
          style={{ maxHeight }}
          className="overflow-hidden transition-[max-height] duration-500 ease-in-out"
        >
          <strong>Absent Dates:</strong>
          {expandedUser.AbsentReasons.length === 0 ? (
            <p className="mt-2 text-gray-500">No absent days so far.</p>
          ) : (
            <div className="flex flex-wrap gap-4 mt-2">
              {Array.from(
                { length: Math.ceil(expandedUser.AbsentReasons.length / 7) },
                (_, colIndex) => (
                  <ul key={colIndex} className="ml-5 list-none">
                    {expandedUser.AbsentReasons
                      .slice(colIndex * 7, colIndex * 7 + 7)
                      .map((d, i) => (
                        <li key={i} className="pb-2 text-left">
                          {d.date.split(",")[0] === "Sunday" ? (
                            <span className="text-pink-700 font-bold">
                              <span className="mr-1">{colIndex * 7 + i + 1}.</span> {d.date}
                            </span>
                          ) : (
                        <>
                          <span className="mr-1">{colIndex * 7 + i + 1}.</span>
                          {d.date}{" "}
                            <span className="text-blue-500 font-semibold px-1 rounded">
                              ({d.reason ? d.reason : "N/A"})
                            </span>
                        </>

                          )}
                        </li>
                      ))}
                  </ul>
                )
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

const AttendanceTable = () => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  const thisMonth = today.getMonth();
  const thisYear = today.getFullYear();

  const [currentMonth, setCurrentMonth] = useState(thisMonth);
  const [currentYear, setCurrentYear] = useState(thisYear);
  const [attendanceData, setAttendanceData] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);

  const [reasonsData, setReasonsData] = useState([]);

  const totalDaysInMonth = useMemo(
    () => new Date(currentYear, currentMonth + 1, 0).getDate(),
    [currentMonth, currentYear]
  );

  const daysSoFar = useMemo(() => {
    return currentMonth === today.getMonth() && currentYear === today.getFullYear()
      ? today.getDate()
      : totalDaysInMonth;
  }, [currentMonth, currentYear, totalDaysInMonth]);

  // Precompute absent lists for each user
const precomputedAbsentLists = useMemo(() => {
  if (!attendanceData) return {};

  const map = {};

  attendanceData.forEach(user => {
    // parse present dates from attendance
    const presentDates = Array.isArray(user.AbsentList)
      ? user.AbsentList.map(d => new Date(d).getDate())
      : (user.AbsentList ? user.AbsentList.split(",").map(d => new Date(d).getDate()) : []);

    // compute absent dates for this month
    const absentList = Array.from({ length: daysSoFar }, (_, i) => i + 1)
      .filter(d => !presentDates.includes(d))
      .map(d => {
        const dateObj = new Date(currentYear, currentMonth, d);
        const weekday = dateObj.toLocaleDateString("en-GB", { weekday: "long" });
        const day = dateObj.getDate();
        const month = dateObj.toLocaleDateString("en-GB", { month: "long" });
        const year = dateObj.getFullYear();
        return `${weekday}, ${day} ${month} ${year}`;
      });

    // map reasons by date string for this user
    const reasonMap = new Map(
      reasonsData
        .filter(r => r.userId === user.id)
        .map(r => [r.date, r.reason])
    );

    // merge reasons into absent list
    const AbsentMerged = absentList.map(dateStr => ({
      date: dateStr,
      reason: reasonMap.get(dateStr) || null
    }));

    map[user.userName] = {
      present: presentDates.length,
      absent: absentList.length,
      totalDays: totalDaysInMonth,
      AbsentReasons: AbsentMerged
    };
  });

  return map;
}, [attendanceData, reasonsData, currentMonth, currentYear, totalDaysInMonth, daysSoFar]);


// Subscribe to dashboard store slice
const attendanceDat = useDashboard(state => state.data?.attendance);

// -------------------------------------
// Process attendanceDat requests data
// -------------------------------------
useEffect(() => {
  if (!attendanceDat) return;

  const month = currentMonth + 1;
  const year = currentYear;

  const stats = attendanceDat.records.reduce((acc, record) => {
    const recDate = new Date(record.attendanceDate);

    // Only consider users in this month/year
    if (recDate.getMonth() + 1 !== month || recDate.getFullYear() !== year) return acc;

    if (!acc[record.userName]) {
      acc[record.userName] = {
        id: record.id,
        userName: record.userName,
        present: 0,
        totalDays: 0,
        AbsentList: [],
      };
    }

    if (record.status === "Present") {
      acc[record.userName].present += 1;
      // Format as YYYY-MM-DD
      const formattedDate = recDate.toISOString().split("T")[0];
      acc[record.userName].AbsentList.push(formattedDate);
    }

    acc[record.userName].totalDays += 1;

    return acc;
  }, {});

  // Compute absent count and join dates
  Object.values(stats).forEach(u => {
    u.absent = u.totalDays - u.present;
    u.AbsentList = u.AbsentList.join(",");
  });

  const statsArray = Object.values(stats);
  setAttendanceData(statsArray);
}, [attendanceDat, currentMonth, currentYear]);



// Subscribe to reasons slice
const reasonsDat = useDashboard(state => state.data?.reasons);

// -------------------------------------
// Process attendanceDat requests data
// -------------------------------------
useEffect(() => {
    if (!reasonsDat) return;

    const filtered = reasonsDat.filter(r => {
        const dateObj = new Date(r.date); // parse string to Date
        return (
            dateObj.getMonth() + 1 === currentMonth + 1 && // JS months are 0-indexed
            dateObj.getFullYear() === currentYear
        );
    });

    setReasonsData(filtered);
}, [reasonsDat, currentMonth, currentYear]);




  const handleRowClick = (user) => {
    if (user?.userName === expandedUser?.userName) {
      setExpandedUser(null);
    } else {
      setExpandedUser({
        ...user,
        ...precomputedAbsentLists[user.userName]
      });
    }
  };
    const handlePrevMonth = () => {
      setCurrentMonth(prev => (prev === 0 ? 11 : prev - 1));
      setExpandedUser(null); // collapse expanded row
    };
    
    const handleNextMonth = () => {
      setCurrentMonth(prev => (prev === 11 ? 0 : prev + 1));
      setExpandedUser(null); // collapse expanded row
    };
    
    const handleCurrent = () => {
      setCurrentMonth(thisMonth);
      setExpandedUser(null); // collapse expanded row
    };


  return (
    <div className="w-full md:max-w-screen p-4 border rounded-md">
      <h2 className="text-base font-bold mb-2">Employee Attendance ({formattedDate})</h2>
      <p className="text-xl font-bold pb-4">
        {currentMonth === thisMonth ? `${months[currentMonth]} (Now)` : months[currentMonth]}
      </p>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-300 ">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-3 py-2 text-left">Employee</th>
              <th className="border border-gray-300 px-3 py-2 text-right">Present</th>
              <th className="border border-gray-300 px-3 py-2 text-right">Absent</th>
              <th className="border border-gray-300 px-3 py-2 text-right">Attendance</th>
              <th className="border border-gray-300 px-3 py-2 text-right">Days So Far</th>
              <th className="border border-gray-300 px-3 py-2 text-right">Total Days</th>
              <th className="border border-gray-300 px-3 py-2 text-right">Show Actions</th>
            </tr>
          </thead>
<tbody>
  {attendanceData.length === 0 ? (
    <tr>
      <td colSpan={7} className="text-center px-3 py-4">
        <div className="text-gray-600 italic text-lg">
          📌 Data is only displayed for the past 3-4 months, not including future months (For performance reasons).
          <br />
          For access to older data, please contact the <span className="font-semibold text-yellow-600">System Admin</span>.
        </div>
      </td>
    </tr>
  ) : (
    attendanceData.map((row, idx) => {
      const isExpanded = expandedUser?.userName === row.userName;
      const rowData = precomputedAbsentLists[row.userName] || {};

      return (
        <React.Fragment key={idx}>
          <tr className="hover:bg-gray-300 even:bg-gray-200 odd:bg-white">
            <td className="border border-gray-300 px-3 py-2 font-medium">{row.userName}</td>
            <td className="border border-gray-300 px-3 py-2 text-right">{rowData.present || 0}</td>
            <td className="border border-gray-300 px-3 py-2 text-right">{daysSoFar - (rowData.present || 0)}</td>
            <td className="border border-gray-300 px-3 py-2 text-right">
              {daysSoFar ? (((rowData.present || 0) / daysSoFar) * 100).toFixed(1) : 0}%
            </td>
            <td className="border border-gray-300 px-3 py-2 text-right">{daysSoFar}</td>
            <td className="border border-gray-300 px-3 py-2 text-right">{totalDaysInMonth}</td>
            <td className="border border-gray-300 px-3 py-2 text-center">
              <button
                onClick={() => handleRowClick(row)}
                className="px-3 py-1 bg-yellow-400 rounded hover:bg-yellow-500 cursor-pointer flex items-center justify-center gap-1 font-semibold"
              >
                {isExpanded ? "Hide Absent" : "Show Absent"}
              </button>
            </td>
          </tr>

          {isExpanded && <ExpandedRow expandedUser={expandedUser} />}
        </React.Fragment>
      );
    })
  )}
</tbody>


        </table>
      </div>

      <div className="py-2 flex justify-between mt-4">
        <button
          onClick={handlePrevMonth}
          className="px-4 py-2 bg-gray-400 cursor-pointer rounded hover:bg-gray-500"
        >
          Previous Month
        </button>
        <button
          onClick={handleCurrent}
          className="px-4 py-2 bg-green-400 cursor-pointer rounded hover:bg-green-500"
        >
          Now
        </button>
        <button
          onClick={handleNextMonth}
          className="px-4 py-2 bg-blue-500 cursor-pointer rounded hover:bg-blue-600"
        >
          Next Month
        </button>
      </div>
    </div>
  );
};

export default AttendanceTable;
